import { Checkbox, Col, Flex, Row, Spin, Typography } from "antd";
import { useEffect, useMemo } from "react";
import { formatUnidad } from "utils";
import useGetAdicionales from "./useGetAdicional";

const { Text } = Typography;

const Adicionales = ({ precioId, unidadMedida, checkedAdicionales, setCheckedAdicionales, styles }) => {
  const { grupos, articulos, unidades, loadingAdicionales, fetchAdicionales } = useGetAdicionales();
  const precio = useMemo(
    () => checkedAdicionales?.find(x => x.id === precioId) || { grupos: [], articulos: [], unidades: [] },
    [checkedAdicionales, precioId]
  );
  const sinGrupos = precio?.grupos?.length === 0;
  const sinArticulos = precio?.articulos?.length === 0;

  const ensurePrecio = (precioId) => {
    const index = checkedAdicionales?.findIndex(x => x.id === precioId);

    if (index === -1) {
      const nuevo = {
        id: precioId,
        grupos: [],
        articulos: [],
        unidades: []
      };

      let newIndex = 0;

      setCheckedAdicionales(prev => {
        const arr = [...prev, nuevo];
        newIndex = arr.length - 1;
        return arr;
      });

      return newIndex;
    }

    return index;
  };

  const toggleItem = (tipo, itemId, isChecked) => {
    const index = ensurePrecio(precioId);

    setCheckedAdicionales(prev => {
      const copia = [...prev];
      const arr = copia[index][tipo];

      // actualizar lista
      const updated = isChecked
        ? [...new Set([...arr, itemId])]
        : arr.filter(id => id !== itemId);

      if (tipo === "grupos" && updated.length === 0) {
        copia[index].articulos = [];
        copia[index].unidades = [];
      }

      if (tipo === "articulos" && updated.length === 0) {
        copia[index].unidades = [];
      }

      // aplicar cambio
      copia[index] = {
        ...copia[index],
        id: precioId,  // ← agregado nuevamente
        [tipo]: updated
      };

      // borrar entrada si queda vacío completamente
      const empty =
        copia[index].grupos.length === 0 &&
        copia[index].articulos.length === 0 &&
        copia[index].unidades.length === 0;

      if (empty) {
        return copia.filter((_, i) => i !== index);
      }

      return copia;
    });
  };

  const selectAll = (tipo, items, isChecked) => {
    const index = ensurePrecio(precioId);

    setCheckedAdicionales(prev => {
      const copia = [...prev];
      const precioActual = { ...copia[index] };

      // si es UNIDADES → usar los valores directos
      const updated =
        tipo === "unidades"
          ? (isChecked ? [...items] : [])
          : (isChecked ? items.map(i => i.id) : []);

      precioActual[tipo] = updated;

      // reglas de cascada
      if (tipo === "grupos" && updated.length === 0) {
        precioActual.articulos = [];
        precioActual.unidades = [];
      }

      if (tipo === "articulos" && updated.length === 0) {
        precioActual.unidades = [];
      }

      // borrar si está vacío
      const empty =
        precioActual.grupos.length === 0 &&
        precioActual.articulos.length === 0 &&
        precioActual.unidades.length === 0;

      if (empty) {
        return copia.filter((_, i) => i !== index);
      }

      copia[index] = precioActual;
      return copia;
    });
  };

  const count = (tipo) => precio?.[tipo]?.length || 0;

  useEffect(() => {
    if (precioId) fetchAdicionales(precioId);
  }, [precioId]);

  useEffect(() => {
    if (!precioId) return;

    // crear estructura vacía si no existe
    const index = ensurePrecio(precioId);

    // si YA tiene datos → no autoseleccionar
    const precio = checkedAdicionales[index];
    if (
      precio?.grupos?.length > 0 ||
      precio?.articulos?.length > 0 ||
      precio?.unidades?.length > 0
    ) {
      return;
    }

    // AUTOCHECK 🔥
    setCheckedAdicionales(prev => {
      const copia = [...prev];

      copia[index] = {
        id: precioId,
        grupos: grupos?.map(g => g.id) || [],
        articulos: articulos?.map(a => a.id) || [],
        unidades: unidades?.map(u => u) || []
      };

      return copia;
    });
  }, [precioId, grupos, articulos, unidades]);

  return (
    <Spin spinning={loadingAdicionales}>
      <Row gutter={24}>
        {/* TRAMOS */}
        <Col xs={9} style={styles.colStyle}>
          <Flex justify="space-between" style={styles.styleSticky}>
            <Flex align="baseline" gap={8}>
              <Checkbox
                checked={precio?.grupos?.length === grupos?.length}
                indeterminate={precio?.grupos?.length > 0 && precio?.grupos?.length < grupos?.length}
                onChange={e => selectAll("grupos", grupos, e.target.checked)}
              />
              <Typography.Text>GRUPOS</Typography.Text>
            </Flex>

            <Text type="secondary">
              {count("grupos")}/{grupos?.length}
            </Text>
          </Flex>

          <Flex vertical gap={6} style={{ padding: '0 12px' }}>
            {grupos?.map(g => (
              <Checkbox
                key={g.id}
                checked={precio?.grupos?.includes(g.id)}
                onChange={e => toggleItem("grupos", g.id, e.target.checked)}
              >
                {g.nombre}
              </Checkbox>
            ))}
          </Flex>
        </Col>

        {/* ARTICULOS */}
        <Col
          xs={9}
          style={{
            ...styles.colStyle,
            borderLeft: "1px solid #D9D9D950",
            borderRight: "1px solid #D9D9D950"
          }}
        >
          <Flex justify="space-between" style={styles.styleSticky}>
            <Flex align="baseline" gap={8}>
              <Checkbox
                disabled={sinGrupos}
                checked={precio?.articulos?.length === articulos?.length}
                indeterminate={precio?.articulos?.length > 0 && precio?.articulos?.length < articulos?.length}
                onChange={e => selectAll("articulos", articulos, e.target.checked)}
              />
              <Typography.Text>ARTÍCULOS</Typography.Text>
            </Flex>

            <Text type="secondary">
              {count("articulos")}/{articulos?.length}
            </Text>
          </Flex>

          <Flex vertical gap={6} style={{ padding: '0 12px' }}>
            {articulos?.map(a => (
              <Checkbox
                key={a.id}
                disabled={sinGrupos}
                checked={precio?.articulos?.includes(a.id)}
                onChange={e => toggleItem("articulos", a.id, e.target.checked)}
              >
                {a.nombre}
              </Checkbox>
            ))}
          </Flex>
        </Col>

        {/* UNIDADES */}
        <Col xs={6} style={styles.colStyle}>
          <Flex justify="space-between" style={styles.styleSticky}>
            <Flex align="baseline" gap={8}>
              <Checkbox
                disabled={sinGrupos || sinArticulos}
                checked={precio?.unidades?.length === unidades?.length}
                indeterminate={precio?.unidades?.length > 0 && precio?.unidades?.length < unidades?.length}
                onChange={e => selectAll("unidades", unidades, e.target.checked)}
              />
              <Typography.Text>UNIDADES</Typography.Text>
            </Flex>

            <Text type="secondary">
              {count("unidades")}/{unidades?.length}
            </Text>
          </Flex>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
              gap: "6px 12px",
              padding: '0 12px'
            }}
          >
            {unidades?.map(unidad => (
              <Checkbox
                key={unidad}
                disabled={sinGrupos || sinArticulos}
                checked={precio?.unidades?.includes(unidad)}
                onChange={e => toggleItem("unidades", unidad, e.target.checked)}
              >
                {formatUnidad(unidadMedida, unidad)}
              </Checkbox>
            ))}
          </div>
        </Col>
      </Row>
    </Spin>
  );
}

export default Adicionales;